<template>
  <v-dialog
    :value="visible"
    persistent
    content-class="task-approve-reject-dialog"
    :data-testid="dialogTestId"
    @input="onDialogInput"
  >
    <v-card
      class="task-approve-reject-dialog__card"
      :loading="saving"
      :disabled="saving"
      loader-height="2"
    >
      <v-card-title class="task-approve-reject-dialog__header white--text">
        <span class="task-approve-reject-dialog__header-title">{{ dialogTitle }}</span>
        <v-spacer />
        <v-btn
          icon
          dark
          class="task-approve-reject-dialog__close-btn"
          aria-label="Close"
          :disabled="saving"
          data-testid="dialog-box-new__close-btn"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="task-approve-reject-dialog__body">
        <div class="task-approve-reject-dialog__hero">
          <div
            class="task-approve-reject-dialog__icon-wrap"
            :class="`task-approve-reject-dialog__icon-wrap--${mode}`"
            data-testid="tasklist__approve-reject-icon"
          >
            <img
              :src="statusIconSrc"
              :alt="dialogTitle"
              class="task-approve-reject-dialog__icon-img"
            />
          </div>

          <h5
            class="task-approve-reject-dialog__title"
            :class="`task-approve-reject-dialog__title--${mode}`"
            data-testid="tasklist__approve-reject-title"
          >
            {{ bodyTitle }}
          </h5>
        </div>

        <p
          class="task-approve-reject-dialog__message"
          data-testid="tasklist__approve-reject-message"
        >
          {{ bodyMessage }}
        </p>

        <div class="task-approve-reject-dialog__remark">
          <CollapsibleTextAreaField
            v-if="isApprove"
            v-model="remark"
            label="Remarks"
            variant="card"
            :expanded.sync="remarksExpanded"
            :rows="3"
            placeholder="Type here.."
            data-testid="tasklist__approve-reject-remark-input"
          />
          <CollapsibleTextAreaField
            v-else
            v-model="remark"
            label="Remarks"
            variant="card"
            :collapsible="false"
            :required="true"
            :rows="3"
            placeholder="eg. Duplicate entry"
            data-testid="tasklist__approve-reject-remark-input"
          />
        </div>
      </v-card-text>

      <v-card-actions class="task-approve-reject-dialog__footer">
        <v-spacer />
        <v-btn
          outlined
          class="task-approve-reject-dialog__cancel-btn text-capitalize secondaryBtn"
          :disabled="saving"
          data-testid="tasklist__approve-reject-cancel-btn"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          class="task-approve-reject-dialog__confirm-btn primaryBtn text-capitalize ml-0"
          :class="{ 'task-approve-reject-dialog__confirm-btn--disabled': isConfirmDisabled }"
          :loading="saving"
          :disabled="isConfirmDisabled"
          :data-testid="confirmTestId"
          @click="confirm"
        >
          Yes, Proceed
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import './TaskApproveRejectDialog.scss';
import { CollapsibleTextAreaField } from '../../../../../components';
import { TASK_STAGES, CHECKLIST_STATUS_ACTION, REJECTION_REMARK } from '../../../../../utils/constants';
import {
  groupTasksByStatusActionType,
  resolveChecklistStatusActionType,
  isEligibleForRejectAction,
  isValidRejectionRemark,
} from '../../../../../utils/tasklistHelpers';

const DIALOG_MODES = [
  CHECKLIST_STATUS_ACTION.APPROVE,
  CHECKLIST_STATUS_ACTION.REJECT,
  CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING,
];

export default {
  name: 'TaskApproveRejectDialog',
  components: {
    CollapsibleTextAreaField,
  },
  props: {
    visible: { type: Boolean, default: false },
    mode: {
      type: String,
      default: CHECKLIST_STATUS_ACTION.APPROVE,
      validator: (v) => DIALOG_MODES.includes(v),
    },
    task: { type: Object, default: () => ({}) },
    tasks: { type: Array, default: () => [] },
    metadata: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      saving: false,
      remark: '',
      remarksExpanded: false,
    };
  },
  computed: {
    resolvedTasks() {
      if (Array.isArray(this.tasks) && this.tasks.length) return this.tasks;
      if (this.task?._id) return [this.task];
      return [];
    },
    eligibleTaskCount() {
      return this.getEligibleTasks(this.resolvedTasks).length;
    },
    isApprove() {
      return this.mode === CHECKLIST_STATUS_ACTION.APPROVE;
    },
    requiresRemark() {
      return !this.isApprove;
    },
    dialogTitle() {
      return this.isApprove ? 'Approve Tasks' : 'Reject Tasks';
    },
    bodyTitle() {
      return this.isApprove ? 'Approve Selected Tasks' : 'Reject Selected Tasks';
    },
    bodyMessage() {
      const count = this.eligibleTaskCount;
      const taskWord = count === 1 ? 'task' : 'tasks';
      const action = this.isApprove ? 'approve' : 'reject';

      if (this.isApprove) {
        return `You are about to ${action} ${count} selected ${taskWord}. This action will update the status of all selected ${taskWord} to Approved. Are you sure you want to proceed?`;
      }

      return `You are about to ${action} ${count} selected ${taskWord}. This action will update the status of all selected ${taskWord} to Rejected and notify assigned users.`;
    },
    statusIconSrc() {
      if (this.isApprove) {
        return '/img/icons/fbc-icons/ApproveTaskIcon.svg';
      }
      return '/img/icons/fbc-icons/RejectTaskIcon.svg';
    },
    dialogTestId() {
      return this.isApprove
        ? 'tasklist__approve-dialog'
        : 'tasklist__reject-dialog';
    },
    confirmTestId() {
      return this.isApprove
        ? 'tasklist__approve-confirm-btn'
        : 'tasklist__reject-confirm-btn';
    },
    trimmedRemark() {
      return (this.remark || '').trim();
    },
    isRemarkValid() {
      if (this.isApprove) return true;
      return isValidRejectionRemark(this.remark);
    },
    isConfirmDisabled() {
      return this.saving || this.eligibleTaskCount === 0 || !this.isRemarkValid;
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.saving = false;
        this.remark = '';
        this.remarksExpanded = false;
      }
    },
  },
  methods: {
    onDialogInput(val) {
      this.$emit('update:visible', val);
      if (!val) this.saving = false;
    },
    close() {
      if (this.saving) return;
      this.$emit('update:visible', false);
    },
    isClosedTask(task) {
      return task?.stage === TASK_STAGES.CLOSED;
    },
    getEligibleTasks(tasks) {
      if (this.isApprove) {
        return tasks.filter((task) => !this.isClosedTask(task));
      }
      return tasks.filter((task) => isEligibleForRejectAction(task));
    },
    async submitBulkStatusChange(tasks) {
      const eligibleTasks = this.getEligibleTasks(tasks);
      const skippedCount = tasks.length - eligibleTasks.length;

      if (eligibleTasks.length === 0) {
        this.$store.commit('sbError', 'Closed tasks cannot be modified');
        return false;
      }

      if (skippedCount > 0) {
        this.$store.commit(
          'sbWarning',
          `${skippedCount} closed task${skippedCount === 1 ? '' : 's'} excluded from this action`
        );
      }

      const actionGroups = groupTasksByStatusActionType(eligibleTasks, this.mode);
      for (const group of actionGroups) {
        const success = await this.$store.dispatch('tasks/bulkChangeChecklistStatus', {
          checklistId: this.metadata._id,
          taskIds: group.tasks.map((item) => item._id),
          status: group.actionType,
          remark: this.trimmedRemark,
        });
        if (!success) return false;
      }

      return true;
    },
    async confirm() {
      const tasks = this.resolvedTasks;
      if (!tasks.length || !this.metadata?._id || this.eligibleTaskCount === 0) return;

      if (!this.isRemarkValid) {
        this.$store.commit(
          'sbError',
          this.trimmedRemark ? REJECTION_REMARK.INVALID : REJECTION_REMARK.REQUIRED
        );
        return;
      }

      this.saving = true;
      try {
        let success = false;
        const eligibleTasks = this.getEligibleTasks(tasks);

        if (eligibleTasks.length === 1) {
          const status = resolveChecklistStatusActionType(eligibleTasks[0], this.mode);
          success = await this.$store.dispatch('tasks/changeChecklistStatus', {
            checklistId: this.metadata._id,
            taskId: eligibleTasks[0]._id,
            status,
            remark: this.trimmedRemark,
          });
        } else {
          success = await this.submitBulkStatusChange(tasks);
        }

        if (success) {
          this.$emit('saved', {
            tasks: eligibleTasks,
            type: this.mode,
            bulk: eligibleTasks.length > 1,
            remark: this.trimmedRemark,
          });
          this.remark = '';
          this.remarksExpanded = false;
          this.$emit('update:visible', false);
        }
      } catch (err) {
        this.$store.commit('sbError', err?.message || err || 'Unknown error!');
        console.log('[TaskApproveRejectDialog]:', err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
